/**
 * SummaryEditor — TipTap rich-text editor dedicated to the AI meeting
 * summary (v3.03). Kept separate from the Tasks / booking editors so the
 * summary can expose its own config without regressing those:
 *   - Headings H3–H6 only (H1 = meeting title, H2 = section).
 *   - bold / italic / underline, bullet + numbered lists, links.
 *   - images: upload to object storage OR insert by URL.
 *   - Emits DOMPurify-sanitized HTML; backward-compatible with plain text
 *     (a bare string is wrapped in <p> so legacy summaries still render).
 */
import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import DOMPurify from "dompurify";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  ImagePlus,
  Heading3,
  Heading4,
  Heading5,
  Heading6,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Quote,
} from "lucide-react";

const HEADING_LEVELS = [3, 4, 5, 6];
const HEADING_ICONS = { 3: Heading3, 4: Heading4, 5: Heading5, 6: Heading6 };
const ALIGN_OPTIONS = [
  { value: "left", Icon: AlignLeft, label: "Align left" },
  { value: "center", Icon: AlignCenter, label: "Align center" },
  { value: "right", Icon: AlignRight, label: "Align right" },
  { value: "justify", Icon: AlignJustify, label: "Justify" },
];

const SANITIZE_OPTS = {
  ALLOWED_TAGS: [
    "p", "br", "strong", "em", "u", "s", "code", "pre", "blockquote",
    "h3", "h4", "h5", "h6", "ul", "ol", "li", "a", "img",
  ],
  // `style` is allowed so TipTap's text-align (inline style) survives; DOMPurify
  // still sanitizes CSS values, so only safe properties like text-align persist.
  ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "class", "style"],
  ALLOW_DATA_ATTR: false,
};

export function sanitizeSummaryHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(String(html), SANITIZE_OPTS);
}

/** Treat a bare string (no tags) as plain text and wrap it for the editor. */
function normalizeIncoming(value) {
  if (value === undefined || value === null) return "";
  const v = String(value);
  if (!v) return "";
  if (v.includes("<") && v.includes(">")) return v;
  const escaped = v
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<p>${escaped.replace(/\n/g, "<br/>")}</p>`;
}

/** Read-only sanitized render of the summary HTML. */
export function SummaryView({ html, className = "" }) {
  const safe = sanitizeSummaryHtml(normalizeIncoming(html));
  if (!safe || safe === "<p></p>") {
    return (
      <p className={`text-sm text-[#82828c] italic ${className}`} data-testid="summary-view-empty">
        No summary yet.
      </p>
    );
  }
  return (
    <div
      className={`bloq-summary-rte prose prose-sm max-w-none ${className}`}
      data-testid="summary-view"
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

function ToolBtn({ on, action, label, children, testid }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        action();
      }}
      title={label}
      aria-label={label}
      data-testid={testid}
      className={`rt-toolbar-btn ${on ? "rt-toolbar-btn--on" : ""}`}
    >
      {children}
    </button>
  );
}

export default function SummaryEditor({ value, onChange, meetingId, testId = "summary-editor" }) {
  const fileInputRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: HEADING_LEVELS }, codeBlock: false }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" },
      }),
      Image.configure({
        inline: false,
        HTMLAttributes: { class: "rounded-lg max-w-full h-auto my-3" },
      }),
      Placeholder.configure({ placeholder: "Edit the summary…" }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: normalizeIncoming(value),
    onUpdate: ({ editor: e }) => {
      onChange && onChange(sanitizeSummaryHtml(e.getHTML()));
    },
    editorProps: {
      attributes: {
        class: "bloq-summary-rte prose prose-sm max-w-none focus:outline-none min-h-[160px] px-3 py-2",
        "data-testid": testId,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = normalizeIncoming(value);
    if (current !== incoming && incoming !== "<p></p>") {
      editor.commands.setContent(incoming || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return <div className="bloq-input min-h-[160px] text-sm text-[#82828c]">Loading editor…</div>;
  }

  const addLink = () => {
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const addImageByUrl = () => {
    const url = window.prompt("Image URL", "https://");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are supported");
      return;
    }
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/summaries/images?meeting_id=${encodeURIComponent(meetingId)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data?.url) editor.chain().focus().setImage({ src: data.url }).run();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    }
  };

  return (
    <div className="rt-wrapper border border-[#1a1a1f]/15 bg-white" data-testid={`${testId}-wrapper`}>
      <div className="rt-toolbar flex flex-wrap gap-0.5 px-2 py-1.5 border-b border-[#1a1a1f]/10 bg-[#f5efe6]/60">
        <ToolBtn on={editor.isActive("bold")} action={() => editor.chain().focus().toggleBold().run()} label="Bold" testid="summary-bold"><Bold className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn on={editor.isActive("italic")} action={() => editor.chain().focus().toggleItalic().run()} label="Italic" testid="summary-italic"><Italic className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn on={editor.isActive("underline")} action={() => editor.chain().focus().toggleUnderline().run()} label="Underline" testid="summary-underline"><UnderlineIcon className="w-3.5 h-3.5" /></ToolBtn>
        <span className="rt-sep" />
        {HEADING_LEVELS.map((lvl) => {
          const Icon = HEADING_ICONS[lvl];
          return (
            <ToolBtn
              key={lvl}
              on={editor.isActive("heading", { level: lvl })}
              action={() => editor.chain().focus().toggleHeading({ level: lvl }).run()}
              label={`Heading ${lvl}`}
              testid={`summary-h${lvl}`}
            >
              <Icon className="w-3.5 h-3.5" />
            </ToolBtn>
          );
        })}
        <span className="rt-sep" />
        <ToolBtn on={editor.isActive("bulletList")} action={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list" testid="summary-ul"><List className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn on={editor.isActive("orderedList")} action={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list" testid="summary-ol"><ListOrdered className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn on={editor.isActive("link")} action={addLink} label="Link" testid="summary-link"><LinkIcon className="w-3.5 h-3.5" /></ToolBtn>
        <span className="rt-sep" />
        {ALIGN_OPTIONS.map(({ value, Icon, label }) => (
          <ToolBtn
            key={value}
            on={editor.isActive({ textAlign: value })}
            action={() => editor.chain().focus().setTextAlign(value).run()}
            label={label}
            testid={`summary-align-${value}`}
          >
            <Icon className="w-3.5 h-3.5" />
          </ToolBtn>
        ))}
        <ToolBtn on={editor.isActive("blockquote")} action={() => editor.chain().focus().toggleBlockquote().run()} label="Blockquote" testid="summary-blockquote"><Quote className="w-3.5 h-3.5" /></ToolBtn>
        <span className="rt-sep" />
        <ToolBtn on={false} action={() => fileInputRef.current?.click()} label="Upload image" testid="summary-image-upload"><ImagePlus className="w-3.5 h-3.5" /></ToolBtn>
        <ToolBtn on={false} action={addImageByUrl} label="Image by URL" testid="summary-image-url"><ImageIcon className="w-3.5 h-3.5" /></ToolBtn>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onPickFile}
        data-testid="summary-image-file-input"
      />
      <EditorContent editor={editor} />
    </div>
  );
}
